<div class="api-keys-page">
  <h3>{{ 'apiKeys.title' | transloco }}</h3>
  <p class="page-lede">{{ 'apiKeys.subtitle' | transloco }}</p>

  <!-- Underline tabs: the boxed default competed with the cards below for attention. -->
  <div class="ak-tabs">
    <button type="button" class="ak-tab" [class.is-active]="activeTab === 'keys'" (click)="onSelectTab('keys')">
      <i class="pi pi-key"></i>{{ 'apiKeys.tabKeys' | transloco }}
    </button>
    <button type="button" class="ak-tab" [class.is-active]="activeTab === 'functions'" (click)="onSelectTab('functions')">
      <i class="pi pi-sitemap"></i>{{ 'apiKeys.tabFunctions' | transloco }}
    </button>
    <button type="button" class="ak-tab" [class.is-active]="activeTab === 'health'" (click)="onSelectTab('health')">
      <i class="pi pi-heart"></i>{{ 'apiKeys.tabHealth' | transloco }}
    </button>
  </div>

  <!-- Saving will fail without a master key; say so before the operator finds out the hard way. -->
  <div *ngIf="dataLoaded && !encryptionConfigured" class="ak-banner is-danger">
    <strong>{{ 'apiKeys.encryptionOffTitle' | transloco }}</strong>
    {{ 'apiKeys.encryptionOffHint' | transloco }}
  </div>

  <!-- ── API Keys ─────────────────────────────────────────────────────────── -->
  <div *ngIf="dataLoaded && activeTab === 'keys'" class="ak-grid">
    <div class="ak-card" *ngFor="let provider of providers">

      <div class="ak-card-head">
        <span class="ak-name">{{ provider.Name }}</span>
        <span class="ak-pill" [ngClass]="pillClass(provider)">{{ sourceLabel(provider) }}</span>
      </div>

      <div class="ak-card-body">
        <!-- Masked, always. No route returns a value, so there is nothing else to show. -->
        <span class="ak-label">{{ 'apiKeys.current' | transloco }}</span>
        <div class="ak-current">
          <ng-container *ngIf="provider.Status?.IsSet; else noKey">
            <span class="ak-masked">{{ maskedValue(provider) }}</span>
            <i class="pi pi-check-circle ak-tick"></i>
          </ng-container>
          <ng-template #noKey>
            <span class="ak-unset">{{ 'apiKeys.none' | transloco }}</span>
          </ng-template>
        </div>

        <div class="ak-meta" *ngIf="provider.Status?.ModifiedDate">
          {{ 'apiKeys.lastChanged' | transloco }}: {{ provider.Status?.ModifiedDate | date:'d MMM y, HH:mm' }}
        </div>

        <!-- Paste and commit are one action, so the input and its button sit together. -->
        <span class="ak-label" style="margin-top:0.55rem;">
          {{ (provider.Status?.Source === 'Stored' ? 'apiKeys.replaceKey' : 'apiKeys.setKey') | transloco }}
        </span>
        <div class="ak-field">
          <input type="password"
                 autocomplete="off"
                 spellcheck="false"
                 [attr.aria-label]="provider.Name"
                 [placeholder]="'apiKeys.pastePlaceholder' | transloco"
                 [(ngModel)]="candidates[provider.CredentialKey]" />
          <button type="button" class="ak-btn is-primary"
                  [disabled]="!hasCandidate(provider) || !encryptionConfigured"
                  (click)="onSave(provider)">
            {{ 'apiKeys.save' | transloco }}
          </button>
        </div>

        <div class="ak-model" *ngIf="provider.DefaultModel">
          <code>{{ provider.DefaultModel }}</code>
          <span *ngIf="provider.CostPerMTokens != null"> · ~${{ provider.CostPerMTokens }}/1M {{ 'apiKeys.tokens' | transloco }}</span>
        </div>

        <div class="ak-actions">
          <!-- The label states WHICH key is about to be tested, so the result is unambiguous. -->
          <button type="button" class="ak-btn is-quiet"
                  [disabled]="testing[provider.Key] || (!hasCandidate(provider) && !provider.Status?.IsSet)"
                  (click)="onTestConnection(provider)">
            <i class="pi pi-bolt" *ngIf="!testing[provider.Key]"></i>
            <i class="pi pi-spin pi-spinner" *ngIf="testing[provider.Key]"></i>
            {{ (hasCandidate(provider) ? 'apiKeys.testTyped' : 'apiKeys.testStored') | transloco }}
          </button>
          <button type="button" class="ak-btn is-danger-link"
                  *ngIf="provider.Status?.Source === 'Stored'"
                  (click)="onRemove(provider)">
            {{ 'apiKeys.remove' | transloco }}
          </button>
        </div>

        <div class="ak-result" *ngIf="probeResults[provider.Key]"
             [class.is-ok]="probeResults[provider.Key].Ok"
             [class.is-fail]="!probeResults[provider.Key].Ok">
          <ng-container *ngIf="probeResults[provider.Key].Ok">
            {{ 'apiKeys.testOk' | transloco }}
            <span *ngIf="probeResults[provider.Key].ResponseTimeMs != null">· {{ probeResults[provider.Key].ResponseTimeMs }}ms</span>
          </ng-container>
          <ng-container *ngIf="!probeResults[provider.Key].Ok">{{ probeResults[provider.Key].Error }}</ng-container>
        </div>
      </div>
    </div>
  </div>

  <!-- ── AI Functions ─────────────────────────────────────────────────────── -->
  <div *ngIf="activeTab === 'functions'">
    <p class="page-lede">{{ 'apiKeys.functionsSubtitle' | transloco }}</p>

    <div class="ak-fn" *ngFor="let fn of functions">
      <div class="ak-fn-head">
        <div>
          <div class="ak-fn-name">{{ fn.Name }}</div>
          <div class="ak-fn-desc">{{ fn.Description }}</div>
        </div>
        <span class="ak-cap">{{ fn.Capability }}</span>
      </div>

      <div class="ak-fn-controls">
        <!-- Only providers that can serve this capability AND are implemented appear here. -->
        <select [(ngModel)]="fn.AssignedProviderKey">
          <option [ngValue]="null">{{ 'apiKeys.useDefault' | transloco }}</option>
          <option *ngFor="let candidate of fn.Candidates" [ngValue]="candidate.Key">{{ candidate.Name }}</option>
        </select>

        <input type="text"
               [placeholder]="'apiKeys.providerDefault' | transloco"
               [(ngModel)]="fn.AssignedModel" />

        <label class="ak-toggle">
          <input type="checkbox" [(ngModel)]="fn.IsEnabled" />
          {{ 'apiKeys.enabled' | transloco }}
        </label>

        <button type="button" class="ak-btn is-primary" style="border-radius: 4px;" (click)="onSaveBinding(fn)">
          {{ 'apiKeys.save' | transloco }}
        </button>
      </div>

      <!-- Why this provider was chosen. Always shown: a silent fallback is what misleads. -->
      <div class="ak-note" *ngIf="fn.Resolution" [ngClass]="noteClass(fn)">
        <strong>{{ 'apiKeys.inUse' | transloco }}:</strong>
        <span *ngIf="fn.Resolution.ProviderName">{{ fn.Resolution.ProviderName }}</span>
        <span *ngIf="fn.Resolution.Model"> · {{ fn.Resolution.Model }}</span>
        — {{ fn.Resolution.ResolutionNote }}
      </div>
    </div>
  </div>

  <!-- ── Health ───────────────────────────────────────────────────────────── -->
  <div *ngIf="activeTab === 'health'">
    <div class="ak-fn" *ngIf="health">
      <div class="ak-health-head">
        <span class="ak-overall">
          {{ 'apiKeys.connectivity' | transloco }}
          <span class="ak-pill" [ngClass]="overallPillClass()">{{ health.Overall }}</span>
        </span>
        <button type="button" class="ak-btn is-quiet" style="width:auto;"
                [disabled]="checkingHealth" (click)="onRunHealthCheck()">
          <i class="pi pi-spin pi-spinner" *ngIf="checkingHealth"></i>
          {{ 'apiKeys.runCheckNow' | transloco }}
        </button>
      </div>

      <!-- A monitor that has stopped is worse than none, so this is stated, not implied. -->
      <div *ngIf="health.IsStale" class="ak-banner is-danger">
        <strong>{{ 'apiKeys.staleTitle' | transloco }}</strong>
        {{ 'apiKeys.staleHint' | transloco }}
      </div>

      <!-- An unwired alert is indistinguishable from a healthy platform until the outage. -->
      <div *ngIf="!health.AlertingConfigured" class="ak-banner is-warn">
        <strong>{{ 'apiKeys.alertingOffTitle' | transloco }}</strong>
        {{ 'apiKeys.alertingOffHint' | transloco }}
      </div>

      <div class="ak-meta" *ngIf="health.LastCheckedDate">
        {{ 'apiKeys.lastChecked' | transloco }}: {{ health.LastCheckedDate | date:'d MMM y, HH:mm' }}
      </div>

      <table class="ak-table">
        <thead>
          <tr>
            <th>{{ 'apiKeys.provider' | transloco }}</th>
            <th>{{ 'apiKeys.status' | transloco }}</th>
            <th>{{ 'apiKeys.lastOk' | transloco }}</th>
            <th>{{ 'apiKeys.detail' | transloco }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let p of health.Providers">
            <td>{{ p.ProviderName }}</td>
            <td><span class="ak-status" [ngClass]="statusClass(p.Status)">{{ p.Status }}</span></td>
            <td>{{ p.LastOkDate ? (p.LastOkDate | date:'d MMM, HH:mm') : '—' }}</td>
            <td>
              <span *ngIf="p.LastError">{{ p.LastError }}</span>
              <span class="ak-status is-failing" *ngIf="p.ConsecutiveFailures > 0">
                · {{ 'apiKeys.realFailures' | transloco }}: {{ p.ConsecutiveFailures }}
              </span>
              <span *ngIf="p.ResponseTimeMs != null"> · {{ p.ResponseTimeMs }}ms</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Spend: the failure that answers HTTP 200 every time. -->
    <div class="ak-fn" *ngIf="spend">
      <div class="ak-fn-head">
        <div class="ak-fn-name">{{ 'apiKeys.spend' | transloco }}</div>
      </div>
      <div class="ak-note" [ngClass]="spend.IsAnomalous ? 'is-fail' : 'is-muted'" style="border-top:0; padding-top:0;">
        {{ spend.Reason }}
      </div>
      <div class="ak-meta">
        {{ 'apiKeys.thisWeek' | transloco }}: {{ spend.GenerationsLast7Days }} ·
        {{ 'apiKeys.lastWeek' | transloco }}: {{ spend.GenerationsPrevious7Days }}
      </div>
    </div>
  </div>

  <!-- Audit trail: who changed what, when. Never carries a value. -->
  <div class="mt-3" *ngIf="dataLoaded && activeTab === 'keys'">
    <button type="button" class="ak-link" (click)="onToggleAudit()">
      {{ (auditVisible ? 'apiKeys.hideAudit' : 'apiKeys.showAudit') | transloco }}
    </button>

    <table class="ak-table mt-2" *ngIf="auditVisible">
      <thead>
        <tr>
          <th>{{ 'apiKeys.auditWhen' | transloco }}</th>
          <th>{{ 'apiKeys.auditCredential' | transloco }}</th>
          <th>{{ 'apiKeys.auditAction' | transloco }}</th>
          <th>{{ 'apiKeys.auditWho' | transloco }}</th>
          <th>{{ 'apiKeys.auditOutcome' | transloco }}</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let entry of auditTrail">
          <td>{{ entry.PerformedDate | date:'d MMM y, HH:mm' }}</td>
          <td><code>{{ entry.CredentialKey }}</code></td>
          <td>{{ entry.Action }}</td>
          <td>{{ entry.PerformedByEmail || entry.PerformedBy }}</td>
          <td>
            <span class="ak-status" [ngClass]="entry.Outcome === 'Succeeded' ? 'is-healthy' : 'is-failing'">{{ entry.Outcome }}</span>
            <div class="ak-meta" *ngIf="entry.Detail">{{ entry.Detail }}</div>
          </td>
        </tr>
        <tr *ngIf="auditTrail.length === 0">
          <td colspan="5" class="ak-empty">{{ 'apiKeys.auditEmpty' | transloco }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
